/**
 * Age & Fit Unified Widget
 * Combines automatic age recommendations with optional interactive fit calculator
 *
 * Features:
 * - Automatic age guidance based on multi-factor calculation (dimensions + power + speed)
 * - Displays physical fit age AND experience requirements separately for clarity
 * - Optional interactive calculator modal (age/height/weight inputs)
 * - Visual fit chart with child marker positioning
 * - UK regulatory compliance integrated naturally (expandable sections)
 * - Handles edge cases (small but powerful vehicles, regulatory 14+ requirement)
 * - Based on UK growth charts and EN 71-1 regulations
 * - British English throughout
 *
 * @version 2.0.0
 */

(function(window) {
  'use strict';

  class AgeFitUnifiedWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Lazy load setting now inherited from config via BaseWidget
      // this.lazyLoad = false; // REMOVED: Let config control lazy loading

      // Product specifications
      this.dimensions = null;
      this.seatHeight = null;
      this.weightCapacity = null;
      this.speed = null;
      this.speedNumeric = null;
      this.hasSpeedLimiter = false;

      // Age data
      this.taggedAge = null; // From product tags (RiiRoo Recommended)
      this.taggedAgeNumeric = null; // Numeric version for comparisons
      this.physicalFitAge = null; // Calculated from dimensions
      this.experienceAge = null; // Based on power/speed requirements
      this.finalRecommendedAge = null; // Combined recommendation

      // Power-related state
      this.batteryVoltage = null;
      this.motorPower = null;
      this.engineCC = null;
      this.powerLevel = null; // 'low', 'medium', 'high', 'extreme'
      this.productType = null; // 'electric' or 'petrol'
      this.experienceRequired = false;

      // Regulatory compliance
      this.isRegulatoryVehicle = false; // Over 16 km/h
      this.complianceBadge = null; // EN 71-1 compliant or 14+ required

      // UK growth data (ages 2-14)
      this.growthData = {
        2: { height: 87, weight: 12.5, inseam: 35 },
        3: { height: 95, weight: 14.5, inseam: 38 },
        4: { height: 103, weight: 16.5, inseam: 42 },
        5: { height: 110, weight: 18.5, inseam: 46 },
        6: { height: 116, weight: 21, inseam: 50 },
        7: { height: 122, weight: 23, inseam: 54 },
        8: { height: 128, weight: 26, inseam: 58 },
        9: { height: 134, weight: 29, inseam: 62 },
        10: { height: 140, weight: 32, inseam: 66 },
        11: { height: 146, weight: 36, inseam: 70 },
        12: { height: 152, weight: 41, inseam: 74 },
        13: { height: 158, weight: 46, inseam: 78 },
        14: { height: 164, weight: 51, inseam: 82 }
      };

      // Calculator state
      this.selectedAge = null;
      this.enteredHeight = null;
      this.enteredWeight = null;
      this.calculationResult = null;

      // Modal ID
      this.modalId = `age-fit-modal-${config.id}`;
    }

    /**
     * Initialize widget - extract and calculate all age/fit guidance
     */
    async init() {
      // Set up product data from config
      this.productData = {
        tags: (this.config.productTags && this.config.productTags.length > 0) ? this.config.productTags : [],
        title: this.config.productTitle || '',
        description: this.config.productDescription || '',
        handle: this.config.productHandle || '',
        metafields: this.config.productMetafields || {}
      };

      // Extract product specifications
      this.dimensions = this.extractDimensions();
      this.seatHeight = this.extractSeatHeight();
      this.weightCapacity = this.extractWeightCapacity();
      this.speed = this.extractSpeed();
      this.speedNumeric = this.parseSpeed(this.speed);
      this.hasSpeedLimiter = this.detectSpeedLimiter();

      // Extract age from tags (RiiRoo Recommended)
      this.taggedAge = this.extractTaggedAge();
      this.taggedAgeNumeric = this.parseAgeNumeric(this.taggedAge);

      // Detect product type and power
      this.productType = this.detectProductType();
      this.vehicleStyle = this.detectVehicleStyle(); // 'motorbike', 'car', 'tractor', 'push', 'unknown'
      if (this.productType === 'electric') {
        this.batteryVoltage = this.extractBatteryVoltage();
        this.motorPower = this.extractMotorPower();
      } else if (this.productType === 'petrol') {
        this.engineCC = this.extractEngineCC();
      }

      // Calculate power level and experience requirements
      this.powerLevel = this.calculatePowerLevel();
      this.experienceRequired = this.checkExperienceRequired();

      // Determine regulatory status
      this.isRegulatoryVehicle = this.speedNumeric && this.speedNumeric > 16;
      this.complianceBadge = this.getComplianceBadge();

      // Calculate age recommendations
      this.physicalFitAge = this.calculatePhysicalFitAge();
      this.experienceAge = this.calculateExperienceAge();
      this.finalRecommendedAge = this.calculateFinalAge();
    }

    /**
     * Helper: Extract value from description using regex
     */
    extractFromDescription(pattern) {
      if (!this.productData.description) return null;
      const match = this.productData.description.match(pattern);
      return match ? match[1].trim() : null;
    }

    /**
     * Extract tagged age from product tags
     * Example tag: "Max User Age_Up to 14 Years Old"
     */
    extractTaggedAge() {
      const maxAgeTags = this.productData.tags?.filter(tag => tag.startsWith('Max User Age_')) || [];
      if (maxAgeTags.length > 0) {
        return maxAgeTags[0].replace('Max User Age_', '');
      }

      const recAgeTags = this.productData.tags?.filter(tag => tag.startsWith('Recommended Age_')) || [];
      if (recAgeTags.length > 0) {
        return recAgeTags[0].replace('Recommended Age_', '');
      }

      const descAge = this.extractFromDescription(/RiiRoo Max Recommended Age:\s*([^<\n]+)/i);
      if (descAge) return descAge;

      return null;
    }

    /**
     * Parse numeric age from age text
     * Handles formats: "Up to 14 Years Old" -> 14, "3-8 Years" -> 8 (max)
     * DEPRECATED: Use parseTaggedAgeStructure() for proper min/max handling
     */
    parseAgeNumeric(ageText) {
      if (!ageText) return null;

      // Match range format (e.g., "3-8")
      const rangeMatch = ageText.match(/(\d+)\s*-\s*(\d+)/);
      if (rangeMatch) {
        return parseInt(rangeMatch[2]); // Return max age
      }

      // Match single number (e.g., "14", "Up to 14 Years Old")
      const singleMatch = ageText.match(/(\d+)/);
      if (singleMatch) {
        return parseInt(singleMatch[1]);
      }

      return null;
    }

    /**
     * Parse tagged age into structured format
     * Returns: { type, min, max, raw, display }
     *
     * Type can be:
     * - 'minimum': "14+" = Legal minimum age requirement
     * - 'maximum': "Up to 14 Years Old" = RiiRoo's maximum recommendation
     * - 'range': "3-8 Years" = Age range
     */
    parseTaggedAgeStructure(ageText) {
      if (!ageText) return null;

      const structure = {
        raw: ageText,
        type: null,
        min: null,
        max: null,
        display: ageText
      };

      // Check for "X+" format (legal minimum requirement)
      const minPlusMatch = ageText.match(/(\d+)\+/);
      if (minPlusMatch) {
        structure.type = 'minimum';
        structure.min = parseInt(minPlusMatch[1]);
        structure.display = `${structure.min}+ Required`;
        return structure;
      }

      // Check for "Up to X" format (maximum recommendation)
      const upToMatch = ageText.match(/up\s+to\s+(\d+)/i);
      if (upToMatch) {
        structure.type = 'maximum';
        structure.max = parseInt(upToMatch[1]);
        structure.display = `Up to ${structure.max} Years`;
        return structure;
      }

      // Check for range format "X-Y" or "X to Y"
      const rangeMatch = ageText.match(/(\d+)\s*(?:-|to)\s*(\d+)/i);
      if (rangeMatch) {
        structure.type = 'range';
        structure.min = parseInt(rangeMatch[1]);
        structure.max = parseInt(rangeMatch[2]);
        structure.display = `${structure.min}-${structure.max} Years`;
        return structure;
      }

      // Single number (treat as maximum)
      const singleMatch = ageText.match(/(\d+)/);
      if (singleMatch) {
        structure.type = 'maximum';
        structure.max = parseInt(singleMatch[1]);
        structure.display = `Up to ${structure.max} Years`;
        return structure;
      }

      return structure;
    }

    /**
     * Extract product dimensions
     */
    extractDimensions() {
      // Try with colon first (old format)
      let descValue = this.extractFromDescription(/Product Dimensions\s*:\s*([^<\n]+)/i);

      // Try with newline (new format)
      if (!descValue) {
        descValue = this.extractFromDescription(/Product Dimensions\s*\n\s*([^<\n]+)/i);
      }

      if (descValue) {
        return this.parseDimensions(descValue);
      }

      if (this.productData.metafields?.specifications?.dimensions) {
        return this.parseDimensions(this.productData.metafields.specifications.dimensions);
      }

      return null;
    }

    /**
     * Parse dimensions string with smart validation
     * Example: "137 x 72 x 44CM" -> { length: 137, width: 72, height: 44, raw: "..." }
     *
     * Uses vehicle-type-aware heuristics to detect if dimensions are in wrong order:
     * - Motorbikes: Narrow, height > width is normal (don't swap)
     * - Cars/Tractors: Wide, height should be < width (swap if wrong)
     * - Push cars: Small, standard proportions
     */
    parseDimensions(dimString) {
      // Get vehicle style to adjust validation rules
      const vehicleStyle = this.vehicleStyle || 'unknown';
      const cleaned = dimString.replace(/cm/gi, '').trim();
      const parts = cleaned.split(/\s*[xX×]\s*/);

      if (parts.length >= 3) {
        const values = [
          parseFloat(parts[0]) || 0,
          parseFloat(parts[1]) || 0,
          parseFloat(parts[2]) || 0
        ].sort((a, b) => b - a); // Sort descending: [largest, middle, smallest]

        const largest = values[0];
        const middle = values[1];
        const smallest = values[2];

        // SMART DETECTION: Use ride-on physics
        // For kids' vehicles: Length > Width, Height is typically smallest or middle

        // Original values in order given
        const val0 = parseFloat(parts[0]) || 0;
        const val1 = parseFloat(parts[1]) || 0;
        const val2 = parseFloat(parts[2]) || 0;

        let length, width, height;
        let detectedFormat = 'L×W×H'; // Default assumption

        // RULE 1: If 3rd value is largest, definitely wrong order
        if (val2 === largest) {
          // Most likely L×H×W (height and width swapped)
          length = val0;
          height = val1;
          width = val2;
          detectedFormat = 'L×H×W (auto-corrected)';
          console.warn(`[AgeFitUnified] Detected L×H×W format, corrected to L×W×H: ${dimString}`);
        }
        // RULE 2: If 2nd value is largest, probably wrong (L×H×W with tall height)
        else if (val1 === largest && val1 > val0) {
          // Unusual: middle value larger than first
          // Likely wrong, but assume standard L×W×H with warning
          length = val0;
          width = val1;
          height = val2;
          detectedFormat = 'L×W×H (unusual: width > length)';
          console.warn(`[AgeFitUnified] Width (${val1}cm) > Length (${val0}cm) - unusual for ride-ons: ${dimString}`);
        }
        // RULE 3: Standard case - assume L×W×H
        else {
          length = val0;
          width = val1;
          height = val2;

          // But validate it makes sense
          // For most ride-ons: height should be smallest or middle value
          if (height > width && height > length) {
            console.warn(`[AgeFitUnified] Height appears largest - check dimension order: ${dimString}`);
          }
        }

        // Additional heuristic checks (vehicle-type aware)
        const heightToWidthRatio = height / width;
        const heightToLengthRatio = height / length;

        // Vehicle-type-aware validation rules
        if (vehicleStyle === 'motorbike') {
          // Motorbikes are NARROW - height > width is NORMAL, don't flag it
          // Only flag if extremely disproportionate (height > width * 2)
          if (heightToWidthRatio > 2.0) {
            console.warn(`[AgeFitUnified] [Motorbike] Height/Width ratio (${heightToWidthRatio.toFixed(2)}) extremely high - check dimension order: ${dimString}`);
          }

          // Motorbikes can be quite tall relative to length
          if (heightToLengthRatio > 1.2) {
            console.warn(`[AgeFitUnified] [Motorbike] Height/Length ratio (${heightToLengthRatio.toFixed(2)}) very high - check dimension order: ${dimString}`);
          }
        } else if (vehicleStyle === 'car' || vehicleStyle === 'tractor' || vehicleStyle === 'truck') {
          // Cars/Tractors/Trucks are WIDE - height should be < width
          // Flag if height > width (likely wrong order)
          if (heightToWidthRatio > 1.0) {
            console.warn(`[AgeFitUnified] [${vehicleStyle}] Height/Width ratio (${heightToWidthRatio.toFixed(2)}) > 1.0. Cars/Tractors should be wider than tall - possible L×H×W format: ${dimString}`);
          }

          // For wide vehicles, height/length ratio should be quite low
          if (heightToLengthRatio > 0.7) {
            console.warn(`[AgeFitUnified] [${vehicleStyle}] Height/Length ratio (${heightToLengthRatio.toFixed(2)}) high - wide vehicles should be low profile: ${dimString}`);
          }
        } else {
          // Unknown/Push cars - use general heuristics
          // Warning if height is suspiciously large compared to width
          if (heightToWidthRatio > 1.5) {
            console.warn(`[AgeFitUnified] [${vehicleStyle}] Height/Width ratio (${heightToWidthRatio.toFixed(2)}) unusually high. Possible L×H×W format: ${dimString}`);
          }

          // For ride-ons, height/length ratio typically < 0.8
          if (heightToLengthRatio > 0.9) {
            console.warn(`[AgeFitUnified] [${vehicleStyle}] Height/Length ratio (${heightToLengthRatio.toFixed(2)}) high - very tall vehicle or wrong format: ${dimString}`);
          }
        }

        // Sanity check: All dimensions should be reasonable for kids' ride-ons
        if (length < 30 || length > 250 || width < 15 || width > 150 || height < 15 || height > 150) {
          console.warn(`[AgeFitUnified] Dimensions outside expected range for ride-on: ${dimString}`);
        }

        return {
          length: length,
          width: width,
          height: height,
          raw: dimString,
          detectedFormat: detectedFormat,
          confidence: this.calculateDimensionConfidence(length, width, height)
        };
      }

      return null;
    }

    /**
     * Calculate confidence score for dimension interpretation
     * Returns 'high', 'medium', or 'low'
     * Now vehicle-type aware to handle motorbikes (tall/narrow) vs cars (wide/low)
     */
    calculateDimensionConfidence(length, width, height) {
      const vehicleStyle = this.vehicleStyle || 'unknown';
      let confidence = 'high';

      if (vehicleStyle === 'motorbike') {
        // Motorbikes: height > width is NORMAL (narrow vehicles)
        // Only flag if height > length (very unusual)
        if (height > length) confidence = 'low';
        if (height > width * 2) confidence = 'medium'; // Extremely tall even for motorbike
        if (width > length * 0.8) confidence = 'medium'; // Motorbikes shouldn't be this wide
      } else if (vehicleStyle === 'car' || vehicleStyle === 'tractor' || vehicleStyle === 'truck') {
        // Cars/Tractors/Trucks: width > height is NORMAL (wide vehicles)
        // Flag if height > width (wrong order)
        if (height > width) confidence = 'low'; // Definitely wrong for wide vehicles
        if (height > length) confidence = 'low';
        if (width > length * 1.2) confidence = 'medium'; // Width shouldn't exceed length much
        if (height / length > 0.7) confidence = 'medium'; // Should be low profile
      } else {
        // Unknown/Push cars - use general heuristics
        if (height > width) confidence = 'medium';
        if (height > length) confidence = 'low';
        if (width > length * 1.2) confidence = 'medium'; // Width shouldn't exceed length much
        if (height / length > 0.8) confidence = 'medium'; // Very tall vehicles are rare
      }

      return confidence;
    }

    /**
     * Extract seat height from description
     */
    extractSeatHeight() {
      // Try "Seat Height" with colon
      let descValue = this.extractFromDescription(/Seat Height\s*:\s*([^<\n]+)/i);

      // Try "Seat Height" with newline
      if (!descValue) {
        descValue = this.extractFromDescription(/Seat Height\s*\n\s*([^<\n]+)/i);
      }

      // Try "Height to Seat" with colon
      if (!descValue) {
        descValue = this.extractFromDescription(/Height to Seat\s*:\s*([^<\n]+)/i);
      }

      // Try "Height to Seat" with newline
      if (!descValue) {
        descValue = this.extractFromDescription(/Height to Seat\s*\n\s*([^<\n]+)/i);
      }

      if (!descValue) return null;

      // Parse range or single value
      const rangeMatch = descValue.match(/(\d+)\s*-\s*(\d+)/);
      if (rangeMatch) {
        let min = parseFloat(rangeMatch[1]);
        let max = parseFloat(rangeMatch[2]);

        // Convert mm to cm if needed
        if (min > 100 || descValue.toLowerCase().includes('mm')) {
          min = min / 10;
          max = max / 10;
        }

        return { min, max, raw: descValue };
      }

      const singleMatch = descValue.match(/(\d+)/);
      if (singleMatch) {
        let value = parseFloat(singleMatch[1]);

        if (value > 100 || descValue.toLowerCase().includes('mm')) {
          value = value / 10;
        }

        return { min: value, max: value, raw: descValue };
      }

      return null;
    }

    /**
     * Extract weight capacity
     */
    extractWeightCapacity() {
      // Try description with colon
      let descValue = this.extractFromDescription(/Max (?:Load Capacity|User Weight)\s*:\s*([^<\n]+)/i);
      if (descValue) {
        const match = descValue.match(/(\d+)/);
        if (match) return parseFloat(match[1]);
      }

      // Try description with newline
      descValue = this.extractFromDescription(/Max (?:Load Capacity|User Weight)\s*\n\s*([^<\n]+)/i);
      if (descValue) {
        const match = descValue.match(/(\d+)/);
        if (match) return parseFloat(match[1]);
      }

      // Try tags
      const maxWeightTags = this.productData.tags?.filter(tag => tag.startsWith('Max User Weight_')) || [];
      if (maxWeightTags.length > 0) {
        const value = maxWeightTags[0].replace('Max User Weight_', '');
        const match = value.match(/(\d+)/);
        if (match) return parseFloat(match[1]);
      }

      return null;
    }

    /**
     * Extract speed from description
     */
    extractSpeed() {
      // Try "Approx Max Speed" with colon
      let descValue = this.extractFromDescription(/(?:Approx )?Max Speed\s*:\s*([^<\n]+)/i);
      if (descValue) return descValue;

      // Try "Approx Max Speed" with newline
      descValue = this.extractFromDescription(/(?:Approx )?Max Speed\s*\n\s*([^<\n]+)/i);
      if (descValue) return descValue;

      // Try "Speed" with colon
      descValue = this.extractFromDescription(/Speed\s*:\s*([^<\n]+KM\/h)/i);
      if (descValue) return descValue;

      // Try "Speed" with newline
      descValue = this.extractFromDescription(/Speed\s*\n\s*([^<\n]+KM\/h)/i);
      if (descValue) return descValue;

      return null;
    }

    /**
     * Parse speed string to numeric km/h value
     * Example: "3 - 6 KM/h" -> 6 (max), "70 KM/h" -> 70
     */
    parseSpeed(speedString) {
      if (!speedString) return null;

      const numbers = speedString.match(/\d+/g);
      if (!numbers || numbers.length === 0) return null;

      // Take maximum if range
      if (numbers.length > 1) {
        return Math.max(...numbers.map(n => parseInt(n)));
      }

      return parseInt(numbers[0]);
    }

    /**
     * Detect speed limiter feature
     */
    detectSpeedLimiter() {
      const description = this.productData.description?.toLowerCase() || '';

      const limiterKeywords = [
        'speed limiter',
        'speed control',
        'parental speed control',
        'parent control',
        'adjustable speed',
        'speed switch',
        'two-speed',
        'high/low speed',
        'low/high speed'
      ];

      return limiterKeywords.some(keyword => description.includes(keyword));
    }

    /**
     * Detect product type
     */
    detectProductType() {
      const desc = this.productData.description || '';
      const tags = this.productData.tags || [];

      if (desc.match(/petrol|fuel type|unleaded|stroke/i) ||
          tags.some(tag => tag.match(/petrol|fuel/i))) {
        return 'petrol';
      }

      return 'electric';
    }

    /**
     * Detect vehicle style for dimension validation
     * Returns: 'motorbike', 'quad', 'car', 'tractor', 'truck', 'push', 'unknown'
     */
    detectVehicleStyle() {
      const title = (this.productData.title || '').toLowerCase();
      const tags = this.productData.tags || [];
      const desc = (this.productData.description || '').toLowerCase();

      // Check product title and tags for vehicle type
      const combined = `${title} ${tags.join(' ')} ${desc}`.toLowerCase();

      // Motorbikes and bikes (usually narrow, height > width is OK)
      if (combined.match(/motorbike|motorcycle|dirt bike|electric bike|ride.?on bike|motor bike/i)) {
        return 'motorbike';
      }

      // Quads and ATVs (wider than bikes, but still sporty)
      if (combined.match(/quad|atv|4.?wheeler|four wheeler/i)) {
        return 'quad';
      }

      // Tractors (wide, boxy)
      if (combined.match(/tractor|digger|bulldozer|excavator|dumper/i)) {
        return 'tractor';
      }

      // Trucks and vans (wide, boxy)
      if (combined.match(/truck|lorry|van|fire engine|ambulance|police car/i)) {
        return 'truck';
      }

      // Cars and SUVs (wider than bikes, standard proportions)
      if (combined.match(/car|jeep|suv|sports car|racing car|ride.?on car|buggy/i)) {
        return 'car';
      }

      // Push cars (no power, usually small)
      if (combined.match(/push car|push along|foot.?to.?floor|walker/i) ||
          this.productData.tags?.some(tag => tag.match(/Push/i))) {
        return 'push';
      }

      return 'unknown';
    }

    /**
     * Extract battery voltage
     */
    extractBatteryVoltage() {
      // Try tags first
      const batteryVoltageTags = this.productData.tags?.filter(tag => tag.startsWith('Battery Voltage_')) || [];
      if (batteryVoltageTags.length > 0) {
        const voltageStr = batteryVoltageTags[0].replace('Battery Voltage_', '');
        const voltageMatch = voltageStr.match(/(\d+)V?/i);
        if (voltageMatch) return parseInt(voltageMatch[1]);
      }

      const batteryTags = this.productData.tags?.filter(tag => tag.startsWith('Battery_')) || [];
      if (batteryTags.length > 0) {
        const voltageStr = batteryTags[0].replace('Battery_', '');
        const voltageMatch = voltageStr.match(/(\d+)V?/i);
        if (voltageMatch) return parseInt(voltageMatch[1]);
      }

      // Try description with colon
      let descValue = this.extractFromDescription(/Battery Voltage\s*:\s*([^<\n]+)/i);
      if (descValue) {
        const voltageMatch = descValue.match(/(\d+)V?/i);
        if (voltageMatch) return parseInt(voltageMatch[1]);
      }

      // Try description with newline
      descValue = this.extractFromDescription(/Battery Voltage\s*\n\s*([^<\n]+)/i);
      if (descValue) {
        const voltageMatch = descValue.match(/(\d+)V?/i);
        if (voltageMatch) return parseInt(voltageMatch[1]);
      }

      // Try title as fallback
      const title = this.productData.title || '';
      const voltageMatch = title.match(/\b(\d+)V\b/i);
      if (voltageMatch) return parseInt(voltageMatch[1]);

      return null;
    }

    /**
     * Extract motor power (watts)
     */
    extractMotorPower() {
      // Try "Motors" with colon
      let descValue = this.extractFromDescription(/Motors\s*:\s*([^<\n]+)/i);
      if (descValue) {
        const match = descValue.match(/(\d+)\s*x\s*(\d+)W/i);
        if (match) {
          return parseInt(match[1]) * parseInt(match[2]);
        }
        const singleMatch = descValue.match(/(\d+)W/i);
        if (singleMatch) return parseInt(singleMatch[1]);
      }

      // Try "Motors" with newline
      descValue = this.extractFromDescription(/Motors\s*\n\s*([^<\n]+)/i);
      if (descValue) {
        const match = descValue.match(/(\d+)\s*x\s*(\d+)W/i);
        if (match) {
          return parseInt(match[1]) * parseInt(match[2]);
        }
        const singleMatch = descValue.match(/(\d+)W/i);
        if (singleMatch) return parseInt(singleMatch[1]);
      }

      // Try "Electric Motor Spec" or "Motor Power" with colon
      descValue = this.extractFromDescription(/(?:Electric Motor Spec|Motor Power)\s*:\s*([^<\n]+)/i);
      if (descValue) {
        const match = descValue.match(/(\d+)W/i);
        if (match) return parseInt(match[1]);
      }

      // Try "Electric Motor Spec" or "Motor Power" with newline
      descValue = this.extractFromDescription(/(?:Electric Motor Spec|Motor Power)\s*\n\s*([^<\n]+)/i);
      if (descValue) {
        const match = descValue.match(/(\d+)W/i);
        if (match) return parseInt(match[1]);
      }

      return null;
    }

    /**
     * Extract engine capacity (CC)
     */
    extractEngineCC() {
      // Try direct CC match first
      const descValue = this.extractFromDescription(/(\d+)\s*cc/i);
      if (descValue) {
        const match = descValue.match(/(\d+)/);
        if (match) return parseInt(match[1]);
      }

      // Try "Engine" label with colon
      let engineValue = this.extractFromDescription(/Engine[^:]*:\s*([^<\n]+)/i);
      if (engineValue) {
        const match = engineValue.match(/(\d+)\s*cc/i);
        if (match) return parseInt(match[1]);
      }

      // Try "Engine" label with newline
      engineValue = this.extractFromDescription(/Engine[^\n]*\n\s*([^<\n]+)/i);
      if (engineValue) {
        const match = engineValue.match(/(\d+)\s*cc/i);
        if (match) return parseInt(match[1]);
      }

      return null;
    }

    /**
     * Calculate power level
     */
    calculatePowerLevel() {
      if (this.productType === 'electric') {
        const voltage = this.batteryVoltage || 0;
        const power = this.motorPower || 0;

        if (voltage >= 60 || power >= 1000) return 'extreme';
        if ((voltage >= 36 && voltage < 60) || (power >= 500 && power < 1000)) return 'high';
        if (voltage >= 24 || (power >= 100 && power < 500)) return 'medium';
        return 'low';

      } else if (this.productType === 'petrol') {
        const cc = this.engineCC || 0;

        if (cc >= 300) return 'extreme';
        if (cc >= 125 && cc < 300) return 'high';
        if (cc >= 50 && cc < 125) return 'medium';
        return 'low';
      }

      return 'low';
    }

    /**
     * Check if experience is required
     */
    checkExperienceRequired() {
      if (this.powerLevel === 'extreme' || this.powerLevel === 'high') {
        return true;
      }

      if (this.speedNumeric && this.speedNumeric > 40) {
        return true;
      }

      return false;
    }

    /**
     * Get compliance badge
     */
    getComplianceBadge() {
      if (this.isRegulatoryVehicle) {
        return '14+ Required';
      }

      if (this.speedNumeric && this.speedNumeric <= 16) {
        return 'EN 71-1 ✓';
      }

      return null;
    }

    /**
     * Calculate physical fit age based on seat height (PRIORITY 1) or dimensions (FALLBACK)
     * ALWAYS applies multi-factor filtering regardless of data source
     */
    calculatePhysicalFitAge() {
      // PRIORITY 1: Seat height calculation (MOST ACCURATE - matches modal calculator)
      if (this.seatHeight) {
        return this.calculateAgeRangeFromSeatHeight();
      }

      // PRIORITY 2: Fallback to multi-factor assessment without seat height
      // Still considers weight capacity, product height, and tagged age constraints
      return this.calculateAgeRangeWithoutSeatHeight();
    }

    /**
     * Calculate age range WITHOUT seat height (fallback when seat height unavailable)
     * Considers: weight capacity, product height, and tagged age constraints
     */
    calculateAgeRangeWithoutSeatHeight() {
      const ages = Object.keys(this.growthData).map(Number);
      const taggedStructure = this.parseTaggedAgeStructure(this.taggedAge);

      // Find ages that meet all available criteria (excluding seat height)
      const suitableAges = [];

      for (const age of ages) {
        const childData = this.growthData[age];
        let isSuitable = true;

        // FACTOR 1: Weight capacity check
        if (this.weightCapacity && childData.weight > this.weightCapacity) {
          isSuitable = false;
        }

        // FACTOR 2: Product height check (is child too tall relative to product?)
        if (this.dimensions && this.dimensions.height && this.dimensions.confidence === 'high') {
          const productHeight = this.dimensions.height;
          const heightRatio = childData.height / productHeight;

          // If child is more than 3x the product height, they're too big
          if (heightRatio > 3) {
            isSuitable = false;
          }
        }

        // FACTOR 3: Tagged age constraints (RiiRoo's recommendations)
        if (taggedStructure) {
          if (taggedStructure.type === 'maximum' && age > taggedStructure.max) {
            isSuitable = false;
          } else if (taggedStructure.type === 'range') {
            if (age < taggedStructure.min || age > taggedStructure.max) {
              isSuitable = false;
            }
          } else if (taggedStructure.type === 'minimum' && age < taggedStructure.min) {
            isSuitable = false;
          }
        }

        // Add to suitable ages if passes all checks
        if (isSuitable) {
          suitableAges.push(age);
        }
      }

      if (suitableAges.length === 0) {
        // No ages meet all criteria - return null
        return null;
      }

      // Get the range of suitable ages
      const minAge = Math.min(...suitableAges);
      const maxAge = Math.max(...suitableAges);

      // Format as age range - use actual calculated range for accuracy
      if (minAge === maxAge) {
        return `${minAge} years`;
      }

      // Return the actual calculated range (more accurate than bucketing)
      return `${minAge}-${maxAge} years`;
    }

    /**
     * Calculate age range from seat height using UK growth data
     * This matches the logic used in the modal calculator for consistency
     * NOW CONSIDERS: seat height, weight capacity, product height, and tagged age constraints
     */
    calculateAgeRangeFromSeatHeight() {
      if (!this.seatHeight) return null;

      const seatHeight = this.seatHeight.min;
      const ages = Object.keys(this.growthData).map(Number);
      const taggedStructure = this.parseTaggedAgeStructure(this.taggedAge);

      // Find ages that meet ALL criteria (multi-factor assessment)
      const suitableAges = [];

      for (const age of ages) {
        const childData = this.growthData[age];
        let isSuitable = true;
        let reasons = [];

        // FACTOR 1: Seat height check (can child reach ground?)
        const childInseam = childData.inseam;
        const clearance = childInseam - seatHeight;
        if (clearance < 0) {
          isSuitable = false;
          reasons.push('seat too high');
        }

        // FACTOR 2: Weight capacity check
        if (this.weightCapacity && childData.weight > this.weightCapacity) {
          isSuitable = false;
          reasons.push('exceeds weight limit');
        }

        // FACTOR 3: Product height check (is child too tall relative to product?)
        if (this.dimensions && this.dimensions.height && this.dimensions.confidence === 'high') {
          const productHeight = this.dimensions.height;
          const heightRatio = childData.height / productHeight;

          // If child is more than 3x the product height, they're too big
          if (heightRatio > 3) {
            isSuitable = false;
            reasons.push('too tall for product');
          }
        }

        // FACTOR 4: Tagged age constraints (RiiRoo's recommendations)
        if (taggedStructure) {
          if (taggedStructure.type === 'maximum' && age > taggedStructure.max) {
            isSuitable = false;
            reasons.push('exceeds recommended age');
          } else if (taggedStructure.type === 'range') {
            if (age < taggedStructure.min || age > taggedStructure.max) {
              isSuitable = false;
              reasons.push('outside recommended range');
            }
          } else if (taggedStructure.type === 'minimum' && age < taggedStructure.min) {
            isSuitable = false;
            reasons.push('below minimum age');
          }
        }

        // Add to suitable ages if passes all checks
        if (isSuitable) {
          suitableAges.push(age);
        }
      }

      if (suitableAges.length === 0) {
        // No ages meet all criteria - return null to indicate no fit data
        return null;
      }

      // Get the range of suitable ages
      const minAge = Math.min(...suitableAges);
      const maxAge = Math.max(...suitableAges);

      // Format as age range - use actual calculated range for accuracy
      if (minAge === maxAge) {
        return `${minAge} years`;
      }

      // Return the actual calculated range (more accurate than bucketing)
      return `${minAge}-${maxAge} years`;
    }

    /**
     * Calculate experience age based on power/speed
     */
    calculateExperienceAge() {
      if (this.experienceRequired) {
        if (this.powerLevel === 'extreme') {
          return 'Experienced adults only';
        }
        return 'Teen/adult with experience';
      }

      if (this.powerLevel === 'medium') {
        return 'Intermediate riders';
      }

      return 'Beginners welcome';
    }

    /**
     * Calculate final recommended age (prioritises tagged age, supplements with calculation)
     */
    calculateFinalAge() {
      // If we have tagged age and physical fit age, combine them intelligently
      if (this.taggedAge && this.physicalFitAge) {
        // If they match closely, return tagged age
        return this.taggedAge;
      }

      // If only tagged age, return it
      if (this.taggedAge) {
        return this.taggedAge;
      }

      // If only physical fit age, return it
      if (this.physicalFitAge) {
        return this.physicalFitAge;
      }

      // Fallback: Use power level to estimate
      if (this.powerLevel === 'extreme') return 'Adults only';
      if (this.powerLevel === 'high') return '12+ years';
      if (this.powerLevel === 'medium') return '8-12 years';
      return '3-8 years';
    }

    /**
     * Calculate all age recommendations
     * Returns structured object with all age concepts separated
     */
    calculateAgeRecommendations() {
      const taggedStructure = this.parseTaggedAgeStructure(this.taggedAge);

      const recommendations = {
        // Tagged age from RiiRoo (with type: min/max/range)
        tagged: taggedStructure,

        // Physical fit from dimensions
        physical: {
          range: this.physicalFitAge,
          display: this.physicalFitAge
        },

        // Experience requirements from power/speed
        experience: {
          level: this.powerLevel,
          required: this.experienceRequired,
          display: this.experienceAge
        },

        // Regulatory status
        regulatory: {
          isRegulated: this.isRegulatoryVehicle,
          badge: this.complianceBadge,
          hasSpeedLimiter: this.hasSpeedLimiter
        },

        // Display strategy
        displayMode: this.determineDisplayMode(taggedStructure)
      };

      return recommendations;
    }

    /**
     * Determine how to display age information based on available data
     * Returns: 'regulatory' | 'dual' | 'comparison' | 'calculated' | 'simple'
     */
    determineDisplayMode(taggedStructure) {
      // Scenario 1: Legal minimum requirement (14+) from tagged age
      if (taggedStructure && taggedStructure.type === 'minimum') {
        return 'regulatory';
      }

      // Scenario 2: High power requiring experience + physical fit mismatch
      // (e.g., small vehicle with powerful motor - physical fit differs from experience)
      if (this.experienceRequired && this.physicalFitAge) {
        return 'dual';
      }

      // Scenario 3: Tagged maximum + physical calculation available
      // Show both RiiRoo's recommendation and calculated fit
      if (taggedStructure && (taggedStructure.type === 'maximum' || taggedStructure.type === 'range') && this.physicalFitAge) {
        return 'comparison';
      }

      // Scenario 4: Only physical fit calculated (no tagged age)
      if (!taggedStructure && this.physicalFitAge) {
        return 'calculated';
      }

      // Scenario 5: Only tagged age (no calculations available)
      // Or fallback for any other case
      return 'simple';
    }

    /**
     * Render widget to container
     */
    render() {
      // Only show widget if we have age data
      if (!this.finalRecommendedAge && !this.physicalFitAge) {
        this.container.style.display = 'none';
        return;
      }

      // Build widget HTML and set content safely
      this.setContent(this.buildWidgetHTML());

      // Build and append modal
      this.buildModal();

      // Attach event listeners
      this.attachEventListeners();

      // Emit render event
      this.emit('age-fit-unified:rendered', {
        taggedAge: this.taggedAge,
        physicalFitAge: this.physicalFitAge,
        experienceAge: this.experienceAge,
        finalRecommendedAge: this.finalRecommendedAge
      });
    }

    /**
     * Build widget HTML structure
     */
    buildWidgetHTML() {
      const recommendations = this.calculateAgeRecommendations();
      const mode = recommendations.displayMode;

      let ageDisplayHTML = this.buildAgeDisplay(recommendations, mode);

      return `
        <div
          class="pw-widget--card pw-age-fit-card"
          role="button"
          tabindex="0"
          aria-label="Click to check if this fits your child"
          style="padding: 20px; cursor: pointer; transition: all 0.2s ease;">
          <!-- Header -->
          <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 20px;">
            <div style="width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; background: rgb(var(--color-drawer-text) / 0.06); border-radius: var(--rounded-button);">
              ${this.getUserCheckIconSVG()}
            </div>
            <div style="flex: 1;">
              <h3 style="margin: 0; font-size: 1.125rem; font-weight: 700; color: rgb(var(--color-drawer-text));">Is This Right for Your Child?</h3>
            </div>
          </div>

          ${ageDisplayHTML}

          <!-- Action Button -->
          <div style="display: flex; justify-content: center; margin-top: 24px;">
            <button
              type="button"
              class="pw-age-fit__button pw-age-fit__button--primary"
              aria-controls="${this.modalId}"
              aria-expanded="false"
              aria-label="Check if this fits your child and view detailed guidance"
              style="padding: 12px 24px; background: rgb(var(--color-base-button)); color: rgb(var(--color-button-text)); border: none; border-radius: var(--rounded-button); font-size: 0.875rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s;"
            >
              <span>Check Fit & Learn More</span>
              ${this.getArrowIconSVG()}
            </button>
          </div>
        </div>
      `;
    }

    /**
     * Build age display HTML based on display mode
     */
    buildAgeDisplay(recommendations, mode) {
      switch (mode) {
        case 'regulatory':
          return this.buildRegulatoryDisplay(recommendations);

        case 'dual':
          return this.buildDualDisplay(recommendations);

        case 'comparison':
          return this.buildComparisonDisplay(recommendations);

        case 'calculated':
          return this.buildCalculatedDisplay(recommendations);

        case 'simple':
        default:
          return this.buildSimpleDisplay(recommendations);
      }
    }

    /**
     * Regulatory display (14+ legal requirement)
     */
    buildRegulatoryDisplay(recommendations) {
      return `
        <!-- Single Simplified Display -->
        <div style="padding: 24px; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); text-align: center;">
          <span style="display: block; font-size: 0.65rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.4); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Legal Requirement</span>
          <div style="font-size: 2.25rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 8px;">
            ${recommendations.tagged.display}
          </div>
          ${recommendations.physical.range ? `
            <span style="display: block; font-size: 0.875rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.5); margin-top: 12px;">
              Physical fit (calculated): ${recommendations.physical.display}
            </span>
          ` : ''}
        </div>
      `;
    }

    /**
     * Dual display (Physical fit + Experience requirements)
     */
    buildDualDisplay(recommendations) {
      return `
        <!-- Single Simplified Display -->
        <div style="padding: 24px; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); text-align: center;">
          <span style="display: block; font-size: 0.65rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.4); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">
            ${recommendations.tagged ? 'Recommended Age' : 'Physical Fit Age'}
          </span>
          <div style="font-size: 2.25rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 8px;">
            ${recommendations.tagged ? recommendations.tagged.display : recommendations.physical.display}
          </div>
          <span style="display: block; font-size: 0.875rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.5);">
            Experience: ${recommendations.experience.display}
          </span>
        </div>
      `;
    }

    /**
     * Comparison display (RiiRoo recommended + Calculated fit)
     */
    buildComparisonDisplay(recommendations) {
      return `
        <!-- Single Simplified Display -->
        <div style="padding: 24px; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); text-align: center;">
          <span style="display: block; font-size: 0.65rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.4); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Recommended Age</span>
          <div style="font-size: 2.25rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 8px;">
            ${recommendations.tagged.display}
          </div>
          ${recommendations.physical.range ? `
            <span style="display: block; font-size: 0.875rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.5); margin-top: 4px;">
              Physical fit (calculated): ${recommendations.physical.display}
            </span>
          ` : ''}
          <span style="display: block; font-size: 0.875rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.5); margin-top: 4px;">
            Experience: ${recommendations.experience.display}
          </span>
        </div>
      `;
    }

    /**
     * Calculated only display (only physical fit calculated)
     */
    buildCalculatedDisplay(recommendations) {
      return `
        <div style="padding: 24px; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); text-align: center;">
          <span style="display: block; font-size: 0.65rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.4); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Physical Fit Age (Calculated)</span>
          <div style="font-size: 2.25rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 8px;">
            ${recommendations.physical.display}
          </div>
          <span style="display: block; font-size: 0.875rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.5);">
            Experience: ${recommendations.experience.display}
          </span>
        </div>
      `;
    }

    /**
     * Simple display (only tagged age or fallback)
     */
    buildSimpleDisplay(recommendations) {
      const displayAge = recommendations.tagged ? recommendations.tagged.display : (recommendations.physical.display || this.finalRecommendedAge);
      const label = recommendations.tagged ? 'Recommended Age' : 'Physical Fit Age (Calculated)';

      return `
        <div style="padding: 24px; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); text-align: center;">
          <span style="display: block; font-size: 0.65rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.4); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">${label}</span>
          <div style="font-size: 2.25rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 8px;">
            ${displayAge}
          </div>
          <span style="display: block; font-size: 0.875rem; font-weight: 400; color: rgb(var(--color-drawer-text) / 0.5);">
            Experience: ${recommendations.experience.display}
          </span>
        </div>
      `;
    }

    /**
     * Build modal and append to body
     */
    buildModal() {
      const modalHTML = `
        <x-modal id="${this.modalId}" class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none" role="dialog" aria-modal="true" aria-label="Age & Fit Calculator" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${this.modalId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <span class="drawer__title heading font-bold title-md">Age & Fit Calculator</span>
              <button class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center" type="button" is="hover-button" aria-controls="${this.modalId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>
            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink">
                ${this.buildModalContent()}
              </div>
            </div>
          </div>
        </x-modal>
      `;

      const modalElement = document.createElement('div');
      modalElement.innerHTML = modalHTML;
      const modalNode = modalElement.firstElementChild;
      if (modalNode) {
        document.body.appendChild(modalNode);
      }
    }

    /**
     * Build modal content
     */
    buildModalContent() {
      let content = '';

      // Hero section
      content += `
        <div style="padding: 24px; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-card); text-align: center;">
          <h2 style="margin: 0 0 8px 0; font-size: 1.5rem; font-weight: 700; color: rgb(var(--color-drawer-text));">
            Will This Fit My Child?
          </h2>
          <p style="margin: 0; font-size: 0.875rem; color: rgb(var(--color-drawer-text) / 0.7);">
            Enter your child's details below for a personalised fit assessment
          </p>
        </div>
      `;

      // Interactive Calculator Form
      content += this.buildCalculatorForm();

      // Results area (hidden until calculation)
      content += `<div id="calculator-results-${this.config.id}" style="display: none;"></div>`;

      // Expandable sections
      content += this.buildExpandableSection('why-age', 'Why This Age Range?', this.buildWhyAgeContent());
      content += this.buildExpandableSection('about-regulations', 'About UK Regulations', this.buildRegulationsContent());
      content += this.buildExpandableSection('experience-levels', 'Understanding Experience Levels', this.buildExperienceContent());

      return content;
    }

    /**
     * Build calculator form
     */
    buildCalculatorForm() {
      return `
        <div class="pw-age-section" style="padding: 20px; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card);">
          <h3 style="margin: 0 0 16px 0; font-size: 1.125rem; font-weight: 700; color: rgb(var(--color-drawer-text));">
            Child's Details
          </h3>
          <form id="fit-calculator-form-${this.config.id}" style="display: grid; gap: 16px;">
            <div>
              <label style="display: block; margin-bottom: 6px; font-size: 0.875rem; font-weight: 600; color: rgb(var(--color-drawer-text));">
                Child's Age
              </label>
              <select
                id="child-age-${this.config.id}"
                required
                style="width: 100%; padding: 10px 12px; border: 1px solid rgb(var(--color-drawer-text) / 0.15); border-radius: var(--rounded-button); font-size: 0.875rem; background: rgb(var(--color-base-background-1)); color: rgb(var(--color-drawer-text));"
              >
                <option value="">Select age...</option>
                ${Array.from({length: 13}, (_, i) => i + 2).map(age => `<option value="${age}">${age} years old</option>`).join('')}
              </select>
            </div>
            <div>
              <label style="display: block; margin-bottom: 6px; font-size: 0.875rem; font-weight: 600; color: rgb(var(--color-drawer-text));">
                Height (cm)
              </label>
              <input
                type="number"
                id="child-height-${this.config.id}"
                placeholder="e.g. 110"
                min="50"
                max="200"
                required
                style="width: 100%; padding: 10px 12px; border: 1px solid rgb(var(--color-drawer-text) / 0.15); border-radius: var(--rounded-button); font-size: 0.875rem; background: rgb(var(--color-base-background-1)); color: rgb(var(--color-drawer-text));"
              >
              <span id="avg-height-${this.config.id}" style="display: block; margin-top: 4px; font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.6);"></span>
            </div>
            <div>
              <label style="display: block; margin-bottom: 6px; font-size: 0.875rem; font-weight: 600; color: rgb(var(--color-drawer-text));">
                Weight (kg)
              </label>
              <input
                type="number"
                id="child-weight-${this.config.id}"
                placeholder="e.g. 18"
                min="5"
                max="100"
                required
                style="width: 100%; padding: 10px 12px; border: 1px solid rgb(var(--color-drawer-text) / 0.15); border-radius: var(--rounded-button); font-size: 0.875rem; background: rgb(var(--color-base-background-1)); color: rgb(var(--color-drawer-text));"
              >
              <span id="avg-weight-${this.config.id}" style="display: block; margin-top: 4px; font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.6);"></span>
            </div>
            <button
              type="submit"
              style="padding: 12px 24px; background: rgb(var(--color-drawer-button-background)); color: rgb(var(--color-drawer-button-text)); border: none; border-radius: var(--rounded-button); font-size: 0.875rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s;"
            >
              Calculate Fit
            </button>
          </form>
          <p style="margin: 12px 0 0 0; font-size: 0.75rem; color: rgb(var(--color-drawer-text) / 0.6); text-align: center;">
            This calculator provides guidance only. Every child develops differently.
          </p>
        </div>
      `;
    }

    /**
     * Build expandable section
     */
    buildExpandableSection(id, title, content) {
      return `
        <details class="pw-expandable" style="padding: 16px; background: rgb(var(--color-drawer-text) / 0.02); border-radius: var(--rounded-card); border: 1px solid rgb(var(--color-drawer-text) / 0.08);">
          <summary style="cursor: pointer; font-size: 1rem; font-weight: 700; color: rgb(var(--color-drawer-text)); list-style: none; display: flex; align-items: center; justify-content: space-between;">
            <span>${title}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="transition: transform 0.2s;">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </summary>
          <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgb(var(--color-drawer-text) / 0.08); font-size: 0.875rem; line-height: 1.6; color: rgb(var(--color-drawer-text) / 0.8);">
            ${content}
          </div>
        </details>
      `;
    }

    /**
     * Build "Why this age range?" content
     */
    buildWhyAgeContent() {
      let content = '<p><strong>Our age recommendations are based on multiple factors:</strong></p><ul style="margin-left: 1.5rem;">';

      if (this.dimensions) {
        content += `<li><strong>Product Dimensions:</strong> ${this.dimensions.raw} - Physical size indicates suitable age range</li>`;
      }

      if (this.powerLevel && this.powerLevel !== 'low') {
        let powerInfo = '';
        if (this.productType === 'electric') {
          if (this.batteryVoltage) powerInfo += `${this.batteryVoltage}V`;
          if (this.motorPower) powerInfo += `${powerInfo ? ' / ' : ''}${this.motorPower}W`;
        } else {
          if (this.engineCC) powerInfo = `${this.engineCC}cc`;
        }
        content += `<li><strong>Power Level:</strong> ${powerInfo} (${this.powerLevel}) - Affects experience requirements</li>`;
      }

      if (this.speed) {
        content += `<li><strong>Speed:</strong> ${this.speed} - Influences safety considerations</li>`;
      }

      content += '</ul><p style="margin-top: 12px;">We combine these factors with UK child growth data to provide accurate guidance.</p>';

      return content;
    }

    /**
     * Build regulations content
     */
    buildRegulationsContent() {
      if (this.isRegulatoryVehicle) {
        return `
          <p><strong>This product exceeds 16 km/h</strong> and is classified as a <strong>powered vehicle</strong>, not a toy, under UK Toys (Safety) Regulations 2011 and EN 71-1.</p>
          <p><strong>UK/EU Speed Regulations:</strong></p>
          <ul style="margin-left: 1.5rem;">
            <li>Ages 3-6: Maximum 6 km/h</li>
            <li>Ages 6-14: Maximum 16 km/h</li>
            <li>14+ years: No limit (not classified as toy)</li>
          </ul>
          ${this.hasSpeedLimiter ? `
            <p style="margin-top: 12px;"><strong>Speed Limiter:</strong> This product features parent-controlled speed adjustment. When set to lower speed, it may suit younger children based on physical size, though 14+ labelling remains due to maximum capability.</p>
          ` : ''}
        `;
      } else {
        return `
          <p>This product complies with EN 71-1 safety standards for ride-on toys.</p>
          <p><strong>UK/EU Speed Regulations:</strong></p>
          <ul style="margin-left: 1.5rem;">
            <li>Ages 3-6: Maximum 6 km/h</li>
            <li>Ages 6-14: Maximum 16 km/h</li>
          </ul>
          <p style="margin-top: 12px;">Our age recommendations align with these safety standards whilst considering product dimensions and power levels.</p>
        `;
      }
    }

    /**
     * Build experience levels content
     */
    buildExperienceContent() {
      return `
        <p><strong>We classify products into experience levels based on power and speed:</strong></p>
        <ul style="margin-left: 1.5rem;">
          <li><strong>Beginners welcome:</strong> Low power (6-12V, <100W) - Safe for first-time riders</li>
          <li><strong>Intermediate riders:</strong> Medium power (24V, 100-500W) - Some experience recommended</li>
          <li><strong>Teen/adult with experience:</strong> High power (36-48V, 500-1000W) - Requires prior experience</li>
          <li><strong>Experienced adults only:</strong> Extreme power (60V+, 1000W+, 300cc+) - Significant training required</li>
        </ul>
        <p style="margin-top: 12px;"><strong>Always ensure:</strong> Appropriate protective gear, adult supervision, and gradual skill progression.</p>
      `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
      // Calculator form submission
      const form = document.getElementById(`fit-calculator-form-${this.config.id}`);
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.handleCalculatorSubmit();
        });

        // Age dropdown change - show average height/weight
        const ageSelect = document.getElementById(`child-age-${this.config.id}`);
        if (ageSelect) {
          ageSelect.addEventListener('change', () => {
            const age = parseInt(ageSelect.value);
            if (age && this.growthData[age]) {
              const avgHeightSpan = document.getElementById(`avg-height-${this.config.id}`);
              const avgWeightSpan = document.getElementById(`avg-weight-${this.config.id}`);
              if (avgHeightSpan) avgHeightSpan.textContent = `Average for age: ${this.growthData[age].height}cm`;
              if (avgWeightSpan) avgWeightSpan.textContent = `Average for age: ${this.growthData[age].weight}kg`;
            }
          });
        }
      }

      // Make age fit card clickable to open modal
      const card = this.container.querySelector('.pw-age-fit-card');
      if (card) {
        // Click event
        card.addEventListener('click', (e) => {
          // Don't trigger if clicking the button
          if (e.target.closest('.pw-age-fit__button')) {
            return;
          }
          const modal = document.getElementById(this.modalId);
          if (modal && !modal.open) {
            modal.show();
          }
        });

        // Keyboard event (Enter or Space)
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });

        // Hover effect - add subtle border
        card.addEventListener('mouseenter', () => {
          card.style.outline = '2px solid rgb(var(--color-drawer-text) / 0.2)';
          card.style.outlineOffset = '-2px';
        });

        card.addEventListener('mouseleave', () => {
          card.style.outline = 'none';
        });
      }
    }

    /**
     * Handle calculator form submission
     */
    handleCalculatorSubmit() {
      // Get input values
      const age = parseInt(document.getElementById(`child-age-${this.config.id}`).value);
      const height = parseFloat(document.getElementById(`child-height-${this.config.id}`).value);
      const weight = parseFloat(document.getElementById(`child-weight-${this.config.id}`).value);

      if (!age || !height || !weight) {
        return;
      }

      // Store entered values for external access (e.g., bundle system helmet selection)
      this.selectedAge = age;
      this.enteredHeight = height;
      this.enteredWeight = weight;

      // Calculate suitability
      const result = this.calculateSuitability(age, height, weight);
      this.calculationResult = result;

      // Emit event for other systems to react (e.g., bundle drawer to refresh helmet options)
      this.emit('age-fit-unified:calculated', {
        age: age,
        height: height,
        weight: weight,
        result: result
      });

      // Also dispatch a global event for systems not using the orchestrator
      document.dispatchEvent(new CustomEvent('agefit:calculated', {
        detail: { age, height, weight, result }
      }));

      // Display results
      const resultsDiv = document.getElementById(`calculator-results-${this.config.id}`);
      if (resultsDiv) {
        resultsDiv.innerHTML = this.buildResultsHTML(result);
        resultsDiv.style.display = 'block';

        // Scroll to results
        resultsDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /**
     * Calculate suitability based on child's measurements
     */
    calculateSuitability(age, height, weight) {
      const checks = {
        height: this.checkHeightFit(age, height),
        weight: this.checkWeightFit(weight),
        age: this.checkAgeFit(age)
      };

      // Determine overall status
      let overallStatus = 'suitable';
      if (checks.height.status === 'unsuitable' || checks.weight.status === 'unsuitable' || checks.age.status === 'unsuitable') {
        overallStatus = 'unsuitable';
      } else if (checks.height.status === 'warning' || checks.weight.status === 'warning' || checks.age.status === 'warning') {
        overallStatus = 'warning';
      }

      return {
        status: overallStatus,
        checks: checks,
        age: age,
        height: height,
        weight: weight
      };
    }

    /**
     * Check height fit (prioritises accurate seat height when available)
     */
    checkHeightFit(age, height) {
      // PRIORITY 1: Seat height check (MOST ACCURATE)
      // This is the gold standard - if available, use it first
      if (this.seatHeight) {
        // Estimate inseam from height (using growth data ratio or 46% fallback)
        let estimatedInseam;
        if (this.growthData[age]) {
          const ratio = this.growthData[age].inseam / this.growthData[age].height;
          estimatedInseam = height * ratio;
        } else {
          estimatedInseam = height * 0.46;
        }

        const seatHeight = this.seatHeight.min;
        const clearance = estimatedInseam - seatHeight;

        if (clearance < 0) {
          return {
            status: 'unsuitable',
            message: `Child's inseam (${Math.round(estimatedInseam)}cm) is shorter than seat height (${seatHeight}cm). Cannot reach ground comfortably.`
          };
        } else if (clearance < 5) {
          return {
            status: 'warning',
            message: `Minimal ground clearance (${Math.round(clearance)}cm). Consider if child will grow into it soon.`
          };
        } else if (clearance > 20) {
          return {
            status: 'warning',
            message: `Significant clearance (${Math.round(clearance)}cm). May outgrow quickly.`
          };
        } else {
          return {
            status: 'suitable',
            message: `Good clearance (${Math.round(clearance)}cm) over seat. Comfortable fit.`
          };
        }
      }

      // PRIORITY 2: Product height check (if dimensions validated with high confidence)
      // Only use if dimension confidence is high (not swapped/suspicious)
      if (this.dimensions && this.dimensions.height && this.dimensions.confidence === 'high') {
        const productHeight = this.dimensions.height;
        const heightRatio = height / productHeight;

        // If child is more than 3.5x the product height, likely too big
        if (heightRatio > 3.5) {
          return {
            status: 'unsuitable',
            message: `Child's height (${height}cm) is too tall for this product (${productHeight}cm tall). Significantly oversized.`
          };
        } else if (heightRatio > 2.5) {
          return {
            status: 'warning',
            message: `Child's height (${height}cm) is quite tall for this product (${productHeight}cm). May be uncomfortable or outgrow quickly.`
          };
        }
      }

      // PRIORITY 3: Product length as rough guide (fallback)
      if (this.dimensions && this.dimensions.length) {
        const productLength = this.dimensions.length;

        // Very rough heuristic: child's height shouldn't be more than 2x product length
        if (height > productLength * 2) {
          return {
            status: 'warning',
            message: `Child's height (${height}cm) may be too large for this product (${productLength}cm long). Limited fit data available.`
          };
        }
      }

      // No reliable dimension data available
      return {
        status: 'unknown',
        message: 'Insufficient product dimension data to assess height fit'
      };
    }

    /**
     * Check weight fit
     */
    checkWeightFit(weight) {
      if (!this.weightCapacity) {
        return { status: 'unknown', message: 'Weight capacity data not available' };
      }

      const percentOfCapacity = (weight / this.weightCapacity) * 100;

      if (weight > this.weightCapacity) {
        return {
          status: 'unsuitable',
          message: `Child's weight (${weight}kg) exceeds maximum capacity (${this.weightCapacity}kg).`
        };
      } else if (percentOfCapacity >= 90) {
        return {
          status: 'warning',
          message: `Close to capacity (${Math.round(percentOfCapacity)}%). Limited room for growth.`
        };
      } else if (percentOfCapacity >= 75) {
        return {
          status: 'warning',
          message: `${Math.round(percentOfCapacity)}% of capacity. Reasonable fit with some growth room.`
        };
      } else {
        return {
          status: 'suitable',
          message: `Well within capacity (${Math.round(percentOfCapacity)}%). Plenty of room for growth.`
        };
      }
    }

    /**
     * Check age fit (updated to handle min/max/range properly)
     */
    checkAgeFit(age) {
      const taggedStructure = this.parseTaggedAgeStructure(this.taggedAge);

      if (!taggedStructure) {
        return { status: 'unknown', message: 'Age range data not available' };
      }

      // Handle minimum age requirement (14+)
      if (taggedStructure.type === 'minimum') {
        if (age < taggedStructure.min) {
          return {
            status: 'unsuitable',
            message: `Legal requirement: Must be ${taggedStructure.min}+ years old. Child (${age} years) does not meet minimum age.`
          };
        } else {
          return {
            status: 'suitable',
            message: `Child (${age} years) meets legal minimum age requirement (${taggedStructure.min}+).`
          };
        }
      }

      // Handle maximum age recommendation (Up to X)
      if (taggedStructure.type === 'maximum') {
        const ageGap = taggedStructure.max - age;

        if (ageGap < -2) {
          return {
            status: 'warning',
            message: `Child (${age} years) is older than recommended maximum (${taggedStructure.max} years). May outgrow quickly.`
          };
        } else if (ageGap >= 5) {
          return {
            status: 'unsuitable',
            message: `Child (${age} years) is significantly younger than recommended range. Not suitable.`
          };
        } else if (ageGap >= 2) {
          return {
            status: 'warning',
            message: `Child (${age} years) is younger than typical range. Ensure adequate coordination.`
          };
        } else {
          return {
            status: 'suitable',
            message: `Child (${age} years) is within recommended age range (up to ${taggedStructure.max} years).`
          };
        }
      }

      // Handle range (X-Y)
      if (taggedStructure.type === 'range') {
        if (age < taggedStructure.min - 1) {
          return {
            status: 'unsuitable',
            message: `Child (${age} years) is below recommended range (${taggedStructure.display}).`
          };
        } else if (age > taggedStructure.max + 2) {
          return {
            status: 'warning',
            message: `Child (${age} years) is above recommended range (${taggedStructure.display}). May outgrow.`
          };
        } else {
          return {
            status: 'suitable',
            message: `Child (${age} years) is within recommended range (${taggedStructure.display}).`
          };
        }
      }

      return { status: 'unknown', message: 'Could not determine age suitability' };
    }

    /**
     * Build results HTML
     */
    buildResultsHTML(result) {
      const statusConfig = {
        suitable: {
          title: 'Great Fit!',
          color: 'rgb(var(--color-status-success))',
          bg: 'rgb(var(--color-status-success) / 0.1)',
          icon: this.getCheckIconSVG()
        },
        warning: {
          title: 'Proceed with Caution',
          color: 'rgb(var(--color-status-warning))',
          bg: 'rgb(var(--color-status-warning) / 0.1)',
          icon: this.getAlertIconSVG()
        },
        unsuitable: {
          title: 'Not Suitable',
          color: 'rgb(var(--color-status-error))',
          bg: 'rgb(var(--color-status-error) / 0.1)',
          icon: this.getAlertIconSVG()
        }
      };

      const config = statusConfig[result.status];

      let html = `
        <div class="pw-age-section" style="padding: 20px; background: ${config.bg}; border: 2px solid ${config.color}; border-radius: var(--rounded-card); margin-top: 16px;">
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
            <div style="color: ${config.color};">
              ${config.icon}
            </div>
            <h3 style="margin: 0; font-size: 1.25rem; font-weight: 700; color: ${config.color};">
              ${config.title}
            </h3>
          </div>

          <div style="display: grid; gap: 12px;">
            ${this.buildCheckResultHTML('Height & Seat Fit', result.checks.height)}
            ${this.buildCheckResultHTML('Weight Capacity', result.checks.weight)}
            ${this.buildCheckResultHTML('Age Range', result.checks.age)}
          </div>

          ${this.experienceRequired ? `
            <div style="margin-top: 16px; padding: 12px; background: rgb(var(--color-drawer-text) / 0.05); border-radius: var(--rounded-button);">
              <p style="margin: 0; font-size: 0.875rem; font-weight: 700; color: rgb(var(--color-drawer-text));">
                ⚡ Experience Required: ${this.experienceAge}
              </p>
            </div>
          ` : ''}
        </div>
      `;

      return html;
    }

    /**
     * Build individual check result HTML
     */
    buildCheckResultHTML(label, check) {
      const statusIcons = {
        suitable: '✓',
        warning: '⚠️',
        unsuitable: '✗',
        unknown: '?'
      };

      const statusColors = {
        suitable: 'rgb(var(--color-status-success))',
        warning: 'rgb(var(--color-status-warning))',
        unsuitable: 'rgb(var(--color-status-error))',
        unknown: 'rgb(var(--color-drawer-text) / 0.4)'
      };

      return `
        <div style="padding: 12px; background: rgb(var(--color-drawer-text) / 0.03); border-radius: var(--rounded-button);">
          <div style="display: flex; align-items: start; gap: 8px;">
            <span style="font-size: 1.25rem; line-height: 1; color: ${statusColors[check.status]};">
              ${statusIcons[check.status]}
            </span>
            <div style="flex: 1;">
              <div style="font-size: 0.875rem; font-weight: 700; color: rgb(var(--color-drawer-text)); margin-bottom: 4px;">
                ${label}
              </div>
              <div style="font-size: 0.875rem; color: rgb(var(--color-drawer-text) / 0.8);">
                ${check.message}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Cleanup
     */
    destroy() {
      const modal = document.getElementById(this.modalId);
      if (modal) {
        modal.remove();
      }

      super.destroy();
    }

    // SVG Icons
    getUserCheckIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="color: rgb(var(--color-drawer-text));">
          <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M17 11L19 13L23 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getCalculatorIconSVG() {
      return `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="color: currentColor;">
          <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" stroke-width="2"/>
          <rect x="8" y="6" width="8" height="2" rx="1" fill="currentColor"/>
          <path d="M7 13H7.01M12 13H12.01M17 13H17.01M7 17H7.01M12 17H12.01M17 17H17.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      `;
    }

    getArrowIconSVG() {
      return `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="color: currentColor;">
          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getCheckIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getAlertIconSVG() {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }
  }

  // Expose widget class
  if (!window.ProductWidgets || !window.ProductWidgets.widgets) {
    window.ProductWidgets = window.ProductWidgets || {};
    window.ProductWidgets.widgets = window.ProductWidgets.widgets || {};
  }

  window.ProductWidgets.widgets.AgeFitUnifiedWidget = AgeFitUnifiedWidget;

})(window);
